import { useState } from "react";
import { Modal } from "../../components/modal";
import { GuiButton } from "../../components/global-ui/gui-button";
import { GuiTextbox } from "../../components/global-ui/gui-textbox";
import { Spinner } from "../../components/loading-spinner";
import actions from "../../actions/actions";
import { showToast } from "../../components/toast";
import genericCardImg from "../../images/credit-card-generic.svg";
import {
  detectCardType,
  formatCardNumber,
  formatExpiry,
  generateProfileName,
} from "../../utilities/card-utils";
import { EXPIRY_MM_YY_REGEX } from "../../utilities/constants";
import "../../styles/add-payment-modal.scss";

function getInitialState(paymentMethod) {
  return {
    cardNumber: "",
    expiry: paymentMethod?.expiryDisplay ?? "",
    cvv: "",
    firstName: "",
    lastName: "",
    postalCode: "",
    profileName: paymentMethod?.profileName ?? "",
    cardType: "", // detected automatically
    authorized: false,
    makeDefault: false,
    errors: {},
    saving: false,
    apiError: null,
  };
}

function validate(state) {
  const errors = {};

  if (!state.cardNumber.trim()) {
    errors.cardNumber = "Card Number is required.";
  } else if (!/^\d{13,19}$/.test(state.cardNumber.replace(/[\s-]/g, ""))) {
    errors.cardNumber = "Card Number must be 13–19 digits.";
  }

  if (!state.expiry.trim()) {
    errors.expiry = "Expiry is required.";
  } else if (!EXPIRY_MM_YY_REGEX.test(state.expiry.trim())) {
    errors.expiry = "Use MM/YY format.";
  }

  if (!state.cvv.trim()) {
    errors.cvv = "CVV is required.";
  } else if (!/^\d{3,4}$/.test(state.cvv.trim())) {
    errors.cvv = "3 or 4 digits.";
  }

  if (!state.firstName.trim()) errors.firstName = "First Name is required.";
  if (!state.lastName.trim()) errors.lastName = "Last Name is required.";
  if (!state.postalCode.trim()) errors.postalCode = "Postal Code is required.";
  if (!state.profileName.trim())
    errors.profileName = "Profile Name is required.";

  if (!state.authorized) {
    errors.authorized = "Authorization is required.";
  }

  return errors;
}

export default function AddPaymentModal({
  onClose,
  onSaved,
  paymentMethod,
  userName,
}) {
  const isEditMode = Boolean(paymentMethod);
  const [state, setState] = useState(() => getInitialState(paymentMethod));
  const cardholderName = `${state.firstName} ${state.lastName}`
    .replace(/\s+/g, " ")
    .trim();

  function updateField(name, value) {
    // Auto-detect card type when card number changes
    if (name === "cardNumber") {
      const detectedType = detectCardType(value);
      const formatted = formatCardNumber(value);

      setState((prev) => {
        const newState = {
          ...prev,
          cardNumber: formatted,
          cardType: detectedType,
          errors: { ...prev.errors, cardNumber: undefined },
          apiError: null,
        };

        // Auto-populate profile name if empty (like legacy system)
        if (detectedType !== "Unknown" && !prev.profileName.trim()) {
          newState.profileName = generateProfileName(formatted, detectedType);
        }

        return newState;
      });
    } else if (name === "expiry") {
      const formatted = formatExpiry(value);
      setState((prev) => ({
        ...prev,
        expiry: formatted,
        errors: { ...prev.errors, expiry: undefined },
        apiError: null,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        [name]: value,
        errors: { ...prev.errors, [name]: undefined },
        apiError: null,
      }));
    }
  }

  function handleCardNumberKeyDown(e) {
    const allowedNavigationKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
    ];

    if (allowedNavigationKeys.includes(e.key)) return;

    // Allow standard shortcuts like Ctrl/Cmd + A/C/V/X
    if (e.ctrlKey || e.metaKey) return;

    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  }

  async function handleSave() {
    const errors = validate(state);
    if (Object.keys(errors).length > 0) {
      setState((prev) => ({ ...prev, errors }));
      return;
    }

    setState((prev) => ({ ...prev, saving: true, apiError: null }));

    let result = null;
    if (isEditMode) {
      const [expiryMonth, expiryYear] = state.expiry.split("/");
      result = await actions.payment.updatePaymentMethod({
        paymentProfileId: paymentMethod.paymentProfileId,
        profileName: state.profileName.trim(),
        firstName: state.firstName.trim(),
        lastName: state.lastName.trim(),
        cardNumber: state.cardNumber.replace(/[\s-]/g, ""),
        expiryMonth: expiryMonth?.trim(),
        expiryYear: expiryYear?.trim(),
        cvv: state.cvv.trim(),
        postalCode: state.postalCode.trim(),
      });
    } else {
      const [expiryMonth, expiryYear] = state.expiry.split("/");
      result = await actions.payment.addPaymentMethod({
        profileName: state.profileName.trim(),
        firstName: state.firstName.trim(),
        lastName: state.lastName.trim(),
        cardNumber: state.cardNumber.replace(/[\s-]/g, ""),
        expiryMonth: expiryMonth?.trim(),
        expiryYear: expiryYear?.trim(),
        cvv: state.cvv.trim(),
        postalCode: state.postalCode.trim(),
        makeDefault: state.makeDefault,
      });
    }

    setState((prev) => ({ ...prev, saving: false }));

    if (result?.success) {
      showToast(
        "success",
        isEditMode
          ? "Payment method updated successfully."
          : "Payment method saved successfully.",
      );
      if (isEditMode) {
        onSaved({
          ...paymentMethod,
          profileName: result.profileName ?? state.profileName.trim(),
          isDefault: result.isDefault ?? paymentMethod.isDefault,
        });
      } else {
        onSaved({
          paymentProfileId: result.paymentProfileId,
          profileName: result.profileName,
          cardType: result.cardType,
          lastFour: result.lastFour,
          expiryDisplay: result.expiryDisplay,
          isDefault: result.isDefault,
        });
      }
    } else {
      setState((prev) => ({
        ...prev,
        apiError:
          result?.message ||
          `Failed to ${isEditMode ? "update" : "save"} payment method. Please try again.`,
      }));
    }
  }

  return (
    <Modal className="add-payment-modal" onClose={onClose}>
      {/* ── Header ── */}
      <div className="apm-header">
        <span className="apm-header__title">
          {isEditMode ? "Edit Payment" : "Add Payment"}
        </span>
        <button
          className="apm-header__close"
          onClick={onClose}
          aria-label="Close"
        >
          <i className="fas fa-times" />
        </button>
      </div>

      {/* ── Body: two columns ── */}
      <div className="apm-body">
        {/* Left column — form */}
        <div className="apm-form">
          <>
            {/* Card Number */}
            <GuiTextbox
              name="cardNumber"
              label="Card Number"
              value={state.cardNumber}
              onChange={updateField}
              onKeyDown={handleCardNumberKeyDown}
              maxLength={19}
              placeholder="0000 0000 0000 0000"
              error={state.errors.cardNumber}
            />
            {state.cardType && state.cardType !== "Unknown" && (
              <div className="apm-form__card-type">
                <small>
                  Card Type: <strong>{state.cardType}</strong>
                </small>
              </div>
            )}

            {/* Expiry + CVV */}
            <div className="apm-form__row">
              <div className="apm-form__expiry">
                <GuiTextbox
                  name="expiry"
                  label="Expiry"
                  value={state.expiry}
                  onChange={updateField}
                  maxLength={5}
                  placeholder="MM/YY"
                  error={state.errors.expiry}
                />
              </div>
              <div className="apm-form__cvv">
                <GuiTextbox
                  name="cvv"
                  label="CVV"
                  value={state.cvv}
                  onChange={updateField}
                  maxLength={4}
                  error={state.errors.cvv}
                />
              </div>
            </div>

            {/* First Name + Last Name */}
            <div className="apm-form__row">
              <GuiTextbox
                name="firstName"
                label="First Name"
                value={state.firstName}
                onChange={updateField}
                error={state.errors.firstName}
              />
              <GuiTextbox
                name="lastName"
                label="Last Name"
                value={state.lastName}
                onChange={updateField}
                error={state.errors.lastName}
              />
            </div>

            {/* Postal Code + Profile Name */}
            <div className="apm-form__row">
              <GuiTextbox
                name="postalCode"
                label="Postal Code"
                value={state.postalCode}
                onChange={updateField}
                error={state.errors.postalCode}
              />
              <GuiTextbox
                name="profileName"
                label="Profile Name"
                value={state.profileName}
                onChange={updateField}
                error={state.errors.profileName}
              />
            </div>

            {/* Authorization */}
            <label
              className={`apm-form__auth${state.errors.authorized ? " apm-form__auth--error" : ""}`}
            >
              <input
                type="checkbox"
                checked={state.authorized}
                onChange={(e) => updateField("authorized", e.target.checked)}
              />
              <span>
                I, {userName || "card holder name"}, authorize Ministry Brands
                Holdings, LLC d/b/a High Ground Solutions (&quot;HGS&quot;) to
                charge my credit card above for agreed upon purchases. I
                understand that my information will be saved for future
                transactions on my account. You agree to allow HGS, our
                affiliates or our service providers to store and process your
                payment information.
              </span>
            </label>
            {state.errors.authorized && (
              <span className="apm-form__auth-error">
                {state.errors.authorized}
              </span>
            )}
          </>
        </div>

        <div className="apm-sidebar">
          <img
            className="apm-sidebar__card-img"
            src={genericCardImg}
            alt="Credit card illustration"
          />
          <label className="apm-sidebar__make-default">
            <input
              type="checkbox"
              checked={state.makeDefault}
              onChange={(e) => updateField("makeDefault", e.target.checked)}
            />
            <span>Make default payment</span>
          </label>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="apm-footer">
        {state.apiError && (
          <span className="apm-form__api-error">{state.apiError}</span>
        )}
        <GuiButton
          className="btn-outline"
          onClick={onClose}
          disabled={state.saving}
        >
          Cancel
        </GuiButton>
        <GuiButton
          className="btn-primary"
          onClick={handleSave}
          disabled={!state.authorized || state.saving}
        >
          {state.saving ? <Spinner /> : "Save Payment Method"}
        </GuiButton>
      </div>
    </Modal>
  );
}
